// Auto-detect the visitor's local currency from browser signals.
// Priority order:
//   1. navigator.language region (e.g. "en-PH" -> PH -> PHP). Most reliable
//      when the visitor's OS/browser locale is set correctly.
//   2. IANA timezone from Intl.DateTimeFormat (e.g. "Asia/Manila" -> PH).
//      Catches travelers whose browser is en-US but who live in Manila.
//   3. Fallback: USD.
//
// This is a pure client heuristic used only for the Landing page audit
// preview. Once the user signs up, their saved settings.currency wins.

const REGION_TO_CCY = {
  // Americas
  US: "USD", CA: "CAD", MX: "MXN", BR: "BRL", AR: "ARS", CL: "CLP",
  CO: "COP", PE: "PEN", UY: "UYU", VE: "VES", BO: "BOB", PY: "PYG",
  EC: "USD", CR: "CRC", GT: "GTQ", DO: "DOP", PA: "PAB", HN: "HNL",
  NI: "NIO", SV: "USD", CU: "CUP", JM: "JMD", TT: "TTD", BB: "BBD",

  // Europe (Eurozone lumped as EUR)
  GB: "GBP", IE: "EUR", FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR",
  NL: "EUR", BE: "EUR", PT: "EUR", GR: "EUR", AT: "EUR", FI: "EUR",
  LU: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", SK: "EUR", SI: "EUR",
  CY: "EUR", MT: "EUR", HR: "EUR", AD: "EUR", MC: "EUR", VA: "EUR",
  SM: "EUR", ME: "EUR", XK: "EUR",
  SE: "SEK", NO: "NOK", DK: "DKK", IS: "ISK", CH: "CHF", LI: "CHF",
  CZ: "CZK", PL: "PLN", HU: "HUF", RO: "RON", BG: "BGN", RS: "RSD",
  BA: "BAM", MK: "MKD", AL: "ALL", MD: "MDL", UA: "UAH", BY: "BYN",
  TR: "TRY", RU: "RUB", GE: "GEL", AM: "AMD", AZ: "AZN",

  // Asia Pacific
  AU: "AUD", NZ: "NZD", FJ: "FJD", PG: "PGK",
  PH: "PHP", SG: "SGD", HK: "HKD", MO: "MOP", MY: "MYR", TH: "THB",
  ID: "IDR", VN: "VND", KH: "KHR", LA: "LAK", MM: "MMK", BN: "BND",
  JP: "JPY", KR: "KRW", KP: "KPW", CN: "CNY", TW: "TWD", MN: "MNT",
  IN: "INR", PK: "PKR", BD: "BDT", LK: "LKR", NP: "NPR", BT: "BTN",
  MV: "MVR", AF: "AFN",

  // Middle East and North Africa
  AE: "AED", SA: "SAR", QA: "QAR", KW: "KWD", BH: "BHD", OM: "OMR",
  IL: "ILS", JO: "JOD", LB: "LBP", SY: "SYP", IQ: "IQD", IR: "IRR",
  YE: "YER", EG: "EGP", MA: "MAD", DZ: "DZD", TN: "TND", LY: "LYD",

  // Sub-Saharan Africa
  ZA: "ZAR", NG: "NGN", KE: "KES", GH: "GHS", ET: "ETB", UG: "UGX",
  TZ: "TZS", RW: "RWF", SN: "XOF", CI: "XOF", ML: "XOF", CM: "XAF",
  BW: "BWP", NA: "NAD", MZ: "MZN", ZM: "ZMW", ZW: "ZWL", MU: "MUR",
  MG: "MGA", SO: "SOS", SD: "SDG",
};

// Coarse IANA timezone -> country region map. Only the timezones most
// likely to reach a restaurant landing page. Ordered by prefix so lookups
// stay short and fast.
const TZ_TO_REGION = {
  // Americas
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Anchorage": "US",
  "America/Honolulu": "US", "America/Detroit": "US", "America/Indianapolis": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA", "America/St_Johns": "CA",
  "America/Mexico_City": "MX", "America/Monterrey": "MX", "America/Cancun": "MX",
  "America/Tijuana": "MX",
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Manaus": "BR", "America/Recife": "BR",
  "America/Argentina/Buenos_Aires": "AR", "America/Santiago": "CL",
  "America/Bogota": "CO", "America/Lima": "PE", "America/Caracas": "VE",
  "America/Havana": "CU", "America/Jamaica": "JM", "America/Panama": "PA",
  "America/Costa_Rica": "CR", "America/Guatemala": "GT", "America/Tegucigalpa": "HN",
  "America/Managua": "NI", "America/El_Salvador": "SV", "America/Santo_Domingo": "DO",
  "America/La_Paz": "BO", "America/Asuncion": "PY", "America/Montevideo": "UY",

  // Europe
  "Europe/London": "GB", "Europe/Dublin": "IE",
  "Europe/Paris": "FR", "Europe/Berlin": "DE", "Europe/Madrid": "ES",
  "Europe/Rome": "IT", "Europe/Amsterdam": "NL", "Europe/Brussels": "BE",
  "Europe/Lisbon": "PT", "Europe/Athens": "GR", "Europe/Vienna": "AT",
  "Europe/Helsinki": "FI", "Europe/Luxembourg": "LU", "Europe/Tallinn": "EE",
  "Europe/Riga": "LV", "Europe/Vilnius": "LT", "Europe/Bratislava": "SK",
  "Europe/Ljubljana": "SI", "Europe/Nicosia": "CY", "Europe/Malta": "MT",
  "Europe/Zagreb": "HR", "Europe/Podgorica": "ME", "Europe/Belgrade": "RS",
  "Europe/Sarajevo": "BA", "Europe/Skopje": "MK", "Europe/Tirane": "AL",
  "Europe/Chisinau": "MD", "Europe/Kyiv": "UA", "Europe/Kiev": "UA",
  "Europe/Minsk": "BY", "Europe/Moscow": "RU", "Europe/Istanbul": "TR",
  "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK",
  "Europe/Reykjavik": "IS", "Europe/Zurich": "CH", "Europe/Vaduz": "LI",
  "Europe/Prague": "CZ", "Europe/Warsaw": "PL", "Europe/Budapest": "HU",
  "Europe/Bucharest": "RO", "Europe/Sofia": "BG", "Europe/Monaco": "MC",
  "Atlantic/Reykjavik": "IS",

  // Asia
  "Asia/Manila": "PH", "Asia/Singapore": "SG", "Asia/Hong_Kong": "HK",
  "Asia/Macau": "MO", "Asia/Kuala_Lumpur": "MY", "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID", "Asia/Makassar": "ID", "Asia/Ho_Chi_Minh": "VN",
  "Asia/Saigon": "VN", "Asia/Phnom_Penh": "KH", "Asia/Vientiane": "LA",
  "Asia/Yangon": "MM", "Asia/Rangoon": "MM", "Asia/Brunei": "BN",
  "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Pyongyang": "KP",
  "Asia/Shanghai": "CN", "Asia/Chongqing": "CN", "Asia/Urumqi": "CN",
  "Asia/Taipei": "TW", "Asia/Ulaanbaatar": "MN",
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD", "Asia/Colombo": "LK", "Asia/Kathmandu": "NP",
  "Asia/Thimphu": "BT", "Asia/Male": "MV", "Asia/Kabul": "AF",
  "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Qatar": "QA",
  "Asia/Kuwait": "KW", "Asia/Bahrain": "BH", "Asia/Muscat": "OM",
  "Asia/Jerusalem": "IL", "Asia/Tel_Aviv": "IL", "Asia/Amman": "JO",
  "Asia/Beirut": "LB", "Asia/Damascus": "SY", "Asia/Baghdad": "IQ",
  "Asia/Tehran": "IR", "Asia/Aden": "YE", "Asia/Yerevan": "AM",
  "Asia/Baku": "AZ", "Asia/Tbilisi": "GE",

  // Africa
  "Africa/Cairo": "EG", "Africa/Casablanca": "MA", "Africa/Algiers": "DZ",
  "Africa/Tunis": "TN", "Africa/Tripoli": "LY", "Africa/Johannesburg": "ZA",
  "Africa/Lagos": "NG", "Africa/Nairobi": "KE", "Africa/Accra": "GH",
  "Africa/Addis_Ababa": "ET", "Africa/Kampala": "UG", "Africa/Dar_es_Salaam": "TZ",
  "Africa/Kigali": "RW", "Africa/Dakar": "SN", "Africa/Abidjan": "CI",
  "Africa/Bamako": "ML", "Africa/Douala": "CM", "Africa/Gaborone": "BW",
  "Africa/Windhoek": "NA", "Africa/Maputo": "MZ", "Africa/Lusaka": "ZM",
  "Africa/Harare": "ZW", "Indian/Mauritius": "MU", "Indian/Antananarivo": "MG",
  "Africa/Mogadishu": "SO", "Africa/Khartoum": "SD",

  // Oceania
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
  "Australia/Brisbane": "AU", "Australia/Adelaide": "AU", "Australia/Hobart": "AU",
  "Australia/Darwin": "AU",
  "Pacific/Auckland": "NZ", "Pacific/Chatham": "NZ", "Pacific/Fiji": "FJ",
  "Pacific/Port_Moresby": "PG",
};

export function detectCurrency() {
  // Signal 1: OS/browser locale (most reliable for people set up locally).
  try {
    if (typeof navigator !== "undefined" && navigator.language) {
      const parts = String(navigator.language).split("-");
      let region = null;
      if (parts.length >= 2) region = parts[parts.length - 1].toUpperCase();
      if (region && REGION_TO_CCY[region]) return REGION_TO_CCY[region];
    }
  } catch (_) {
    // fall through
  }

  // Signal 2: IANA timezone (catches travelers with a mismatched locale).
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TZ_TO_REGION[tz]) {
      const region = TZ_TO_REGION[tz];
      if (REGION_TO_CCY[region]) return REGION_TO_CCY[region];
    }
  } catch (_) {
    // fall through
  }

  return "USD";
}

// Symbol-only helper for compact hero displays. Falls back to the ISO
// code prefix if Intl can't render a symbol (rare).
export function currencySymbol(code) {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).formatToParts(1);
    const sym = parts.find((p) => p.type === "currency");
    return sym ? sym.value : code;
  } catch (_) {
    return code;
  }
}
